import { getCookie, setCookie } from "@/services/CookiesService";
import { decodeJwt } from "@/services/JwtService";

import { DEFAULT_LANGUAGE } from "@/internals/constants/language";
import { NEXT_LOCALE } from "@/internals/constants/cookies";
import { isSupportedLanguage } from "@/utils/locale";
import patchUserInformationHandler from "@/interface/api/patchUserInformation";
import { safeLogError } from "@/services/DatadogService/safeLogger";

import type { LanguageCode } from "@/types/languages";

const LANGUAGE_COOKIE_MAX_AGE_DAYS = 365;

const addDays = (days: number) => {
  const expiration = new Date();
  expiration.setDate(expiration.getDate() + days);
  return expiration;
};

export const getLanguageCookie = (): LanguageCode | null => {
  const cookieValue = getCookie(NEXT_LOCALE);
  return isSupportedLanguage(cookieValue as string) ? (cookieValue as LanguageCode) : null;
};

export const setLanguageCookie = (locale: LanguageCode) => {
  setCookie(NEXT_LOCALE, locale, addDays(LANGUAGE_COOKIE_MAX_AGE_DAYS));
};

export const resolveStoredLanguage = (): LanguageCode => {
  const stored = getLanguageCookie();
  return stored ?? DEFAULT_LANGUAGE;
};

/**
 * Syncs the user's locale preference on login.
 * Compares the token locale with NEXT_LOCALE cookie and updates if different.
 * This is a non-critical operation - errors are silently logged.
 */
export const syncLocalePreferenceOnLogin = async (token: string): Promise<void> => {
  try {
    const decoded = decodeJwt(token);
    const tokenLocale = decoded?.locale;
    const cookieLocale = getLanguageCookie();

    if (!cookieLocale || !isSupportedLanguage(cookieLocale)) return;
    if (tokenLocale === cookieLocale) return;

    await patchUserInformationHandler.patchUserInformation({
      authorization: token,
      locale: cookieLocale,
    });
  } catch (error) {
    safeLogError(error instanceof Error ? error : new Error(String(error)), {
      service: "LanguagePreferenceService",
      action: "syncLocalePreferenceOnLogin",
    });
  }
};
